import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getTransactions, processRefund } from "../../../data";
import { AdminLayout } from "../shared/layout";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import {
  DollarSign,
  Filter,
  Eye,
  RotateCcw,
  CreditCard,
  TrendingUp,
  XCircle,
  CheckCircle,
} from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";

export function TransactionListScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState({
    status: "",
    platform: "",
    search: "",
    startDate: "",
    endDate: "",
    page: 1,
    limit: 20,
  });

  const [showFilters, setShowFilters] = useState(false);
  const [refundDialog, setRefundDialog] = useState({ open: false, transaction: null });
  const [refundData, setRefundData] = useState({ reason: "", amount: "" });

  const { data, isLoading } = useQuery({
    queryKey: ["transactions", filters],
    queryFn: () => getTransactions(filters),
  });

  const refundMutation = useMutation({
    mutationFn: ({ id, reason, amount }) => processRefund(id, reason, amount),
    onSuccess: () => {
      queryClient.invalidateQueries(["transactions"]);
      setRefundDialog({ open: false, transaction: null });
      setRefundData({ reason: "", amount: "" });
      toast({
        title: "Başarılı",
        description: "İade işlemi tamamlandı",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "İade işlemi başarısız",
        variant: "destructive",
      });
    },
  });

  const handleRefund = (transaction) => {
    setRefundDialog({ open: true, transaction });
    setRefundData({ reason: "", amount: transaction.amount.toString() });
  };

  const handleConfirmRefund = () => {
    refundMutation.mutate({
      id: refundDialog.transaction.id,
      reason: refundData.reason,
      amount: parseFloat(refundData.amount),
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      COMPLETED: { variant: "success", label: "Tamamlandı", icon: CheckCircle },
      PENDING: { variant: "warning", label: "Beklemede", icon: CreditCard },
      FAILED: { variant: "destructive", label: "Başarısız", icon: XCircle },
      REFUNDED: { variant: "secondary", label: "İade Edildi", icon: RotateCcw },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center w-fit gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getPlatformBadge = (platform) => {
    const colors = {
      web: "bg-blue-100 text-blue-800",
      mobile: "bg-green-100 text-green-800",
      ios: "bg-gray-100 text-gray-800",
      android: "bg-orange-100 text-orange-800",
    };

    return (
      <Badge variant="outline" className={colors[platform?.toLowerCase()] || ""}>
        {platform || "N/A"}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd MMM yyyy, HH:mm", { locale: tr });
    } catch (error) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const transactions = data?.data?.data || [];
  const pagination = data?.data?.pagination || {};

  return (
    <AdminLayout
      title="Transactions"
      description="Ödeme işlemlerini görüntüleyin ve yönetin"
      loading={isLoading}
    >
      <div className="space-y-6">
        {/* Quick Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Toplam İşlem</CardTitle>
              <CreditCard className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pagination.total || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Filtreler</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="mr-2 h-4 w-4" />
                {showFilters ? "Gizle" : "Göster"}
              </Button>
            </div>
          </CardHeader>
          {showFilters && (
            <CardContent>
              <div className="grid gap-4 md:grid-cols-4">
                <div className="space-y-2">
                  <Label>Durum</Label>
                  <Select
                    value={filters.status || "all"}
                    onValueChange={(value) =>
                      setFilters({ ...filters, status: value === "all" ? "" : value, page: 1 })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Tüm durumlar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      <SelectItem value="COMPLETED">Tamamlandı</SelectItem>
                      <SelectItem value="PENDING">Beklemede</SelectItem>
                      <SelectItem value="FAILED">Başarısız</SelectItem>
                      <SelectItem value="REFUNDED">İade Edildi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Platform</Label>
                  <Select
                    value={filters.platform || "all"}
                    onValueChange={(value) =>
                      setFilters({ ...filters, platform: value === "all" ? "" : value, page: 1 })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Tüm platformlar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tümü</SelectItem>
                      <SelectItem value="web">Web</SelectItem>
                      <SelectItem value="mobile">Mobile</SelectItem>
                      <SelectItem value="ios">iOS</SelectItem>
                      <SelectItem value="android">Android</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Başlangıç Tarihi</Label>
                  <Input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) =>
                      setFilters({ ...filters, startDate: e.target.value, page: 1 })
                    }
                  />
                </div>

                <div className="space-y-2">
                  <Label>Bitiş Tarihi</Label>
                  <Input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) =>
                      setFilters({ ...filters, endDate: e.target.value, page: 1 })
                    }
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Ara (Transaction ID veya Email)</Label>
                  <Input
                    placeholder="Ara..."
                    value={filters.search}
                    onChange={(e) =>
                      setFilters({ ...filters, search: e.target.value, page: 1 })
                    }
                  />
                </div>

                <div className="md:col-span-2 flex items-end">
                  <Button
                    variant="outline"
                    onClick={() =>
                      setFilters({
                        status: "",
                        platform: "",
                        search: "",
                        startDate: "",
                        endDate: "",
                        page: 1,
                        limit: 20,
                      })
                    }
                  >
                    Filtreleri Temizle
                  </Button>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Transactions Table */}
        <Card>
          <CardHeader>
            <CardTitle>İşlem Listesi</CardTitle>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>Henüz işlem bulunmuyor.</p>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Transaction ID</TableHead>
                      <TableHead>Kullanıcı</TableHead>
                      <TableHead>Tutar</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>Platform</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead>İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                            {transaction.transactionId}
                          </code>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{transaction.user?.name || "N/A"}</p>
                            <p className="text-sm text-gray-500">{transaction.user?.email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(transaction.amount)}
                        </TableCell>
                        <TableCell>{getStatusBadge(transaction.status)}</TableCell>
                        <TableCell>{getPlatformBadge(transaction.platform)}</TableCell>
                        <TableCell>{formatDate(transaction.createdAt)}</TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/admin/transactions/${transaction.id}`)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {transaction.status === "COMPLETED" && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRefund(transaction)}
                                className="text-orange-500 hover:text-orange-600"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-gray-500">
                      Sayfa {pagination.page} / {pagination.totalPages} (Toplam:{" "}
                      {pagination.total})
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.page === 1}
                        onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                      >
                        Önceki
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.page === pagination.totalPages}
                        onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                      >
                        Sonraki
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Refund Dialog */}
      <Dialog
        open={refundDialog.open}
        onOpenChange={(open) => {
          setRefundDialog({ open, transaction: refundDialog.transaction });
          if (!open) setRefundData({ reason: "", amount: "" });
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>İade İşlemi</DialogTitle>
            <DialogDescription>
              Bu işlem için iade yapmak istediğinizden emin misiniz?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {refundDialog.transaction && (
              <div className="bg-gray-50 p-3 rounded-md space-y-2">
                <p className="text-sm">
                  <span className="font-medium">Transaction ID:</span>{" "}
                  {refundDialog.transaction.transactionId}
                </p>
                <p className="text-sm">
                  <span className="font-medium">Kullanıcı:</span>{" "}
                  {refundDialog.transaction.user?.email}
                </p>
                <p className="text-sm">
                  <span className="font-medium">Tutar:</span>{" "}
                  {formatCurrency(refundDialog.transaction.amount)}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="refund-amount">İade Tutarı</Label>
              <Input
                id="refund-amount"
                type="number"
                step="0.01"
                placeholder="Tutar..."
                value={refundData.amount}
                onChange={(e) => setRefundData({ ...refundData, amount: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="refund-reason">Sebep *</Label>
              <Textarea
                id="refund-reason"
                placeholder="İade sebebini girin..."
                value={refundData.reason}
                onChange={(e) => setRefundData({ ...refundData, reason: e.target.value })}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRefundDialog({ open: false, transaction: null });
                setRefundData({ reason: "", amount: "" });
              }}
            >
              İptal
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmRefund}
              disabled={refundMutation.isLoading || !refundData.reason || !refundData.amount}
            >
              {refundMutation.isLoading ? "İşleniyor..." : "İade Et"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

